/**
 * CircularRingRenderer — Generic SVG renderer for circular rings
 *
 * Renders concentric circular rings divided into equal sectors with labels.
 * Designed for Hora Clock and future ring visualizations (nakshatra, 60-sector, etc.).
 *
 * Key Principles:
 * 1. ZERO BUSINESS LOGIC — only rendering
 * 2. DATA-DRIVEN — sectors, labels, colors all from data
 * 3. GENERIC — works for any sector count and ring dimensions
 * 4. COMPOSABLE — multiple rings can be nested by using different radii
 */

import * as d3 from 'd3';
import { FONTS } from '../../../../../config/fonts';
import {
  RING_CENTER_X, RING_CENTER_Y, CircularRingConfig,
  HoraClockDesignConfig, DEFAULT_HORA_CLOCK_DESIGN, resolveHoraSectorColor,
} from '../../../../../config/horaClockConfig';
import { globalEventStream } from '../core/EventStream';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CircularRingSector {
  index: number;
  label: string;
  sublabel?: string;
  topLabel?: string;      // Above sublabel (e.g. house number "1"-"12")
  fillColor?: string;
  highlighted?: boolean;
}

export interface CircularRingRadialLine {
  name: string;
  angleDeg: number;       // 0 = 12 o'clock, clockwise
  color: string;
  strokeWidth: number;
  opacity?: number;
  label?: string;
}

export interface CircularRingMinuteTicks {
  count: number;            // Total tick marks (e.g., 60)
  currentMinute?: number;   // 0-59, for minute hand position
}

export interface CircularRingInfoLabel {
  text: string;           // e.g. "5h 28m 27s"
  title?: string;         // e.g. "Elapsed Since Sunrise"
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export interface CircularRingTattvaIcon {
  angleDeg: number;       // 0 = 12 o'clock, clockwise
  iconPath: string;       // e.g. "/tattvas/agni.png"
}

export type HoraClockHourMode = 12 | 24;

export interface CircularRingData {
  ringType: string;
  chartId: string;
  sectors: CircularRingSector[];
  radialLines?: CircularRingRadialLine[];
  minuteTicks?: CircularRingMinuteTicks;
  config: CircularRingConfig;
  highlightedSectorIndex?: number;
  designConfig?: HoraClockDesignConfig;
  infoLabels?: CircularRingInfoLabel[];
  tattvaIcons?: CircularRingTattvaIcon[];
  labelFont?: string;       // Override font for main label (e.g. symbols font for planets)
  sublabelFont?: string;    // Override font for sublabel (e.g. symbols font for rashi)
  hourMode?: HoraClockHourMode;  // 12 or 24 hour mode (for mode toggle badges)
}

// ─────────────────────────────────────────────────────────────────────────────
// Renderer
// ─────────────────────────────────────────────────────────────────────────────

export class CircularRingRenderer {
  private chartId: string;
  private ringType: string;
  private layer: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  private isVisible: boolean = false;

  constructor(
    chartId: string,
    ringType: string,
    layer: d3.Selection<SVGGElement, unknown, HTMLElement, any>
  ) {
    this.chartId = chartId;
    this.ringType = ringType;
    this.layer = layer;
  }

  /**
   * Render ring from data: sectors + optional radial lines
   */
  renderFromData(data: CircularRingData): void {
    this.clear();

    const { sectors, config, radialLines, highlightedSectorIndex } = data;
    const design = data.designConfig ?? DEFAULT_HORA_CLOCK_DESIGN;
    const sectorCount = sectors.length;
    const sectorAngle = (2 * Math.PI) / sectorCount;

    // Background (full viewBox)
    if (design.backgroundColor && design.backgroundOpacity > 0) {
      this.layer.append('rect')
        .attr('x', 0).attr('y', 0)
        .attr('width', RING_CENTER_X * 2).attr('height', RING_CENTER_Y * 2)
        .attr('fill', design.backgroundColor)
        .attr('fill-opacity', design.backgroundOpacity)
        .attr('class', `${this.ringType}-background`);
    }

    // Sector group
    const sectorsGroup = this.layer.append('g')
      .attr('class', `${this.ringType}-sectors`);

    // Division lines group (on top of sectors)
    const linesGroup = this.layer.append('g')
      .attr('class', `${this.ringType}-division-lines`);

    // Labels group (on top of lines)
    const labelsGroup = this.layer.append('g')
      .attr('class', `${this.ringType}-labels`);

    // Draw sectors
    sectors.forEach((sector, i) => {
      // Clockwise from 12 o'clock: start at -PI/2
      const startAngle = -Math.PI / 2 + i * sectorAngle;
      const endAngle = startAngle + sectorAngle;

      const arcGenerator = d3.arc<any>()
        .innerRadius(config.innerRadius)
        .outerRadius(config.outerRadius)
        .startAngle(startAngle + Math.PI / 2)  // d3.arc uses 0=12 o'clock
        .endAngle(endAngle + Math.PI / 2);

      const isHighlighted = highlightedSectorIndex === i;
      const fillColor = sector.fillColor
        || resolveHoraSectorColor(design, i, sector.sublabel || '', isHighlighted);
      const fillOpacity = isHighlighted ? design.sectorHighlightOpacity : design.sectorOpacity;

      sectorsGroup.append('path')
        .attr('d', arcGenerator({} as any))
        .attr('transform', `translate(${RING_CENTER_X}, ${RING_CENTER_Y})`)
        .attr('fill', fillColor)
        .attr('fill-opacity', fillOpacity)
        .attr('stroke', fillColor)
        .attr('stroke-width', design.sectorStrokeWidth)
        .attr('stroke-opacity', design.sectorStrokeOpacity)
        .attr('class', `${this.ringType}-sector sector-${i}${isHighlighted ? ' highlighted' : ''}`);

      // Curved labels along the center arc of the ring
      const labelRadius = (config.innerRadius + config.outerRadius) / 2;
      const sublabelRadius = labelRadius + design.sublabelOffset;

      // All labels use the same font size (labelSize)
      const uniformSize = design.labelSize;

      // Main label (planet code, e.g. "Ma")
      this.renderCurvedLabel(
        labelsGroup, i, startAngle, endAngle, labelRadius,
        sector.label,
        uniformSize,
        isHighlighted ? '700' : '500',
        isHighlighted ? design.labelHighlightColor : design.labelColor,
        design.labelOpacity,
        `${this.ringType}-label label-${i}`,
        data.labelFont
      );

      // Sublabel (rashi name / symbol)
      if (sector.sublabel) {
        this.renderCurvedLabel(
          labelsGroup, i, startAngle, endAngle, sublabelRadius,
          sector.sublabel,
          uniformSize, '500', design.sublabelColor,
          design.sublabelOpacity,
          `${this.ringType}-sublabel sublabel-${i}`,
          data.sublabelFont
        );
      }

      // Top label (house number, e.g. "1"-"12") — below main label (toward inner edge)
      if (sector.topLabel) {
        const topLabelRadius = labelRadius - design.sublabelOffset;
        this.renderCurvedLabel(
          labelsGroup, i, startAngle, endAngle, topLabelRadius,
          sector.topLabel,
          uniformSize, '500', design.sublabelColor,
          design.sublabelOpacity,
          `${this.ringType}-toplabel toplabel-${i}`
        );
      }
    });

    // Draw division lines between sectors
    for (let i = 0; i < sectorCount; i++) {
      const angle = -Math.PI / 2 + i * sectorAngle;
      const x1 = RING_CENTER_X + config.innerRadius * Math.cos(angle);
      const y1 = RING_CENTER_Y + config.innerRadius * Math.sin(angle);
      const x2 = RING_CENTER_X + config.outerRadius * Math.cos(angle);
      const y2 = RING_CENTER_Y + config.outerRadius * Math.sin(angle);

      linesGroup.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', design.divisionColor)
        .attr('stroke-width', design.divisionWidth)
        .attr('opacity', design.divisionOpacity)
        .attr('class', `${this.ringType}-division division-${i}`);
    }

    // Draw outer and inner circle borders
    linesGroup.append('circle')
      .attr('cx', RING_CENTER_X)
      .attr('cy', RING_CENTER_Y)
      .attr('r', config.outerRadius)
      .attr('fill', 'none')
      .attr('stroke', design.borderColor)
      .attr('stroke-width', design.borderWidth)
      .attr('opacity', design.borderOpacity);

    linesGroup.append('circle')
      .attr('cx', RING_CENTER_X)
      .attr('cy', RING_CENTER_Y)
      .attr('r', config.innerRadius)
      .attr('fill', 'none')
      .attr('stroke', design.borderColor)
      .attr('stroke-width', design.borderWidth)
      .attr('opacity', design.borderOpacity);

    // Draw minute tick marks on the outer edge
    if (data.minuteTicks) {
      this.renderMinuteTicks(linesGroup, data.minuteTicks, config, design);
    }

    // Draw tattva icons at 5-minute marks
    if (data.tattvaIcons?.length) {
      this.renderTattvaIcons(data.tattvaIcons, config, design);
    }

    // Draw radial lines (e.g., ML line)
    if (radialLines) {
      const radialGroup = this.layer.append('g')
        .attr('class', `${this.ringType}-radial-lines`);

      radialLines.forEach(line => {
        this.renderRadialLine(radialGroup, line, config);
      });
    }

    // Draw minute hand
    if (data.minuteTicks?.currentMinute != null) {
      const radialGroup = this.layer.select(`.${this.ringType}-radial-lines`).empty()
        ? this.layer.append('g').attr('class', `${this.ringType}-radial-lines`)
        : this.layer.select(`.${this.ringType}-radial-lines`) as any;

      const minuteAngleDeg = (data.minuteTicks.currentMinute / 60) * 360;
      this.renderRadialLine(radialGroup, {
        name: 'minute',
        angleDeg: minuteAngleDeg,
        color: design.minuteHandColor,
        strokeWidth: design.minuteHandWidth,
        opacity: design.minuteHandOpacity,
        label: 'M',
      }, config);
    }

    // Draw info labels (e.g. "Elapsed Since Sunrise: 5h 28m 27s")
    if (data.infoLabels?.length) {
      this.renderInfoLabels(data.infoLabels, design);
    }

    // Draw 12/24 mode toggle badges above the ring
    if (data.hourMode) {
      this.renderModeToggle(data.hourMode, data.chartId, design);
    }

    this.isVisible = true;
  }

  /**
   * Render a radial line from center to outer edge
   */
  private renderRadialLine(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    line: CircularRingRadialLine,
    config: CircularRingConfig
  ): void {
    // Convert degrees (0=12 o'clock, clockwise) to radians
    const angleRad = (line.angleDeg - 90) * (Math.PI / 180);

    const x1 = RING_CENTER_X;
    const y1 = RING_CENTER_Y;
    const x2 = RING_CENTER_X + (config.outerRadius + 8) * Math.cos(angleRad);
    const y2 = RING_CENTER_Y + (config.outerRadius + 8) * Math.sin(angleRad);

    group.append('line')
      .attr('x1', x1)
      .attr('y1', y1)
      .attr('x2', x2)
      .attr('y2', y2)
      .attr('stroke', line.color)
      .attr('stroke-width', line.strokeWidth)
      .attr('opacity', line.opacity ?? 1)
      .attr('stroke-linecap', 'round')
      .attr('class', `${this.ringType}-radial-line radial-${line.name}`);

    // Label at the tip of the line
    if (line.label) {
      const labelDist = config.outerRadius + 18;
      const lx = RING_CENTER_X + labelDist * Math.cos(angleRad);
      const ly = RING_CENTER_Y + labelDist * Math.sin(angleRad);

      group.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-family', FONTS.chart.primary)
        .attr('font-size', '9px')
        .attr('font-weight', '700')
        .attr('fill', line.color)
        .attr('class', `${this.ringType}-radial-label`)
        .text(line.label);
    }
  }

  /**
   * Render a curved text label along an arc at a given radius
   * Uses SVG <textPath> on a hidden arc path for text-on-path rendering.
   * For bottom-half sectors (text would be upside down), the arc is reversed.
   */
  private renderCurvedLabel(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    sectorIndex: number,
    startAngle: number,
    endAngle: number,
    radius: number,
    text: string,
    fontSize: string,
    fontWeight: string,
    fill: string,
    opacity: number,
    className: string,
    fontFamily?: string
  ): void {
    const midAngle = (startAngle + endAngle) / 2;
    // Determine if the text would be upside down (bottom half of circle)
    const isFlipped = midAngle > 0 && midAngle < Math.PI;

    // Create arc path for textPath
    // For flipped sectors, reverse the arc direction so text reads left-to-right
    const arcStart = isFlipped ? endAngle : startAngle;
    const arcEnd = isFlipped ? startAngle : endAngle;

    const sx = RING_CENTER_X + radius * Math.cos(arcStart);
    const sy = RING_CENTER_Y + radius * Math.sin(arcStart);
    const ex = RING_CENTER_X + radius * Math.cos(arcEnd);
    const ey = RING_CENTER_Y + radius * Math.sin(arcEnd);

    // SVG arc: large-arc=0 (small arc), sweep depends on direction
    const sweep = isFlipped ? 0 : 1;
    const pathId = `${this.ringType}-arc-${sectorIndex}-${Math.round(radius)}`;
    const arcD = `M ${sx} ${sy} A ${radius} ${radius} 0 0 ${sweep} ${ex} ${ey}`;

    // Add hidden path definition
    const defs = group.select('defs').empty()
      ? group.append('defs')
      : group.select('defs');
    defs.append('path')
      .attr('id', pathId)
      .attr('d', arcD);

    // Add text on path
    const textEl = group.append('text')
      .attr('font-family', fontFamily || FONTS.chart.primary)
      .attr('font-size', fontSize)
      .attr('font-weight', fontWeight)
      .attr('fill', fill)
      .attr('opacity', opacity)
      .attr('class', className);

    textEl.append('textPath')
      .attr('href', `#${pathId}`)
      .attr('startOffset', '50%')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .text(text);
  }

  /**
   * Render minute tick marks on the outer edge of the ring
   */
  private renderMinuteTicks(
    group: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    ticks: CircularRingMinuteTicks,
    config: CircularRingConfig,
    design: HoraClockDesignConfig
  ): void {
    const ringWidth = config.outerRadius - config.innerRadius;
    const tickZone = ringWidth * design.tickZone;
    const tickOuterR = config.outerRadius;

    for (let i = 0; i < ticks.count; i++) {
      const angleDeg = (i / ticks.count) * 360;
      const angleRad = (angleDeg - 90) * (Math.PI / 180);

      // Every 5th tick is longer (like 5-minute marks on a clock)
      const isMajor = i % 5 === 0;
      const tickLength = isMajor ? tickZone : tickZone * 0.5;
      const tickInnerR = tickOuterR - tickLength;

      const x1 = RING_CENTER_X + tickInnerR * Math.cos(angleRad);
      const y1 = RING_CENTER_Y + tickInnerR * Math.sin(angleRad);
      const x2 = RING_CENTER_X + tickOuterR * Math.cos(angleRad);
      const y2 = RING_CENTER_Y + tickOuterR * Math.sin(angleRad);

      group.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', isMajor ? design.majorTickColor : design.minorTickColor)
        .attr('stroke-width', isMajor ? design.majorTickWidth : design.minorTickWidth)
        .attr('opacity', design.tickOpacity)
        .attr('class', `${this.ringType}-minute-tick${isMajor ? ' major' : ''}`);
    }
  }

  /**
   * Render tattva icons as content of minute sectors, aligned along the curve
   * like sector labels — centered in each 6° mini-sector, rotated to follow the arc
   */
  private renderTattvaIcons(
    icons: CircularRingTattvaIcon[],
    config: CircularRingConfig,
    design?: HoraClockDesignConfig
  ): void {
    const ringWidth = config.outerRadius - config.innerRadius;
    const tickZone = ringWidth * (design?.tickZone ?? 0.3);
    const iconSize = tickZone ;
    const halfIcon = iconSize / 2;
    // Place at the center of the tick zone band
    const iconRadius = config.outerRadius - tickZone * 0.5;
    const sectorSpan = 6; // each minute sector = 6°

    const group = this.layer.append('g')
      .attr('class', `${this.ringType}-tattva-icons`);

    for (const icon of icons) {
      // Center of the minute sector
      const midAngleDeg = icon.angleDeg + sectorSpan / 2;
      const angleRad = (midAngleDeg - 90) * (Math.PI / 180);
      const cx = RING_CENTER_X + iconRadius * Math.cos(angleRad);
      const cy = RING_CENTER_Y + iconRadius * Math.sin(angleRad);

      // Rotate to follow the curve; flip in bottom half so icons stay upright
      const isBottom = midAngleDeg > 90 && midAngleDeg < 270;
      const rotation = isBottom ? midAngleDeg + 180 : midAngleDeg;

      group.append('image')
        .attr('href', icon.iconPath)
        .attr('x', -halfIcon)
        .attr('y', -halfIcon)
        .attr('width', iconSize)
        .attr('height', iconSize)
        .attr('transform', `translate(${cx},${cy}) rotate(${rotation})`)
        .attr('class', `${this.ringType}-tattva-icon`);
    }
  }

  /**
   * Render info labels in corners of the viewBox
   */
  private renderInfoLabels(
    labels: CircularRingInfoLabel[],
    design: HoraClockDesignConfig
  ): void {
    const padding = 12;
    const viewW = RING_CENTER_X * 2;
    const viewH = RING_CENTER_Y * 2;

    const group = this.layer.append('g')
      .attr('class', `${this.ringType}-info-labels`);

    for (const label of labels) {
      const pos = label.position ?? 'top-right';
      const isRight = pos.includes('right');
      const isBottom = pos.includes('bottom');
      const anchor = isRight ? 'end' : 'start';
      const x = isRight ? viewW - padding : padding;
      let y = isBottom ? viewH - padding : padding;

      // Title line (small, muted)
      if (label.title) {
        group.append('text')
          .attr('x', x)
          .attr('y', y)
          .attr('text-anchor', anchor)
          .attr('dominant-baseline', 'hanging')
          .attr('font-family', FONTS.chart.primary)
          .attr('font-size', '9px')
          .attr('font-weight', '400')
          .attr('fill', design.sublabelColor)
          .attr('opacity', design.sublabelOpacity)
          .attr('class', `${this.ringType}-info-title`)
          .text(label.title);
        y += 14;
      }

      // Value line (larger, bold)
      group.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', anchor)
        .attr('dominant-baseline', 'hanging')
        .attr('font-family', FONTS.chart.primary)
        .attr('font-size', '12px')
        .attr('font-weight', '600')
        .attr('fill', design.labelColor)
        .attr('opacity', design.labelOpacity)
        .attr('class', `${this.ringType}-info-value`)
        .text(label.text);
    }
  }

  /**
   * Render 12/24 hour mode toggle badges centered above the ring
   * Style matches StartPointSelector / PlanetMovementSettings badges
   */
  private renderModeToggle(
    activeMode: HoraClockHourMode,
    chartId: string,
    design: HoraClockDesignConfig
  ): void {
    const group = this.layer.append('g')
      .attr('class', `${this.ringType}-mode-toggle`);

    const modes: HoraClockHourMode[] = [12, 24];
    const badgeW = 26;
    const badgeH = 16;
    const gap = 4;
    const totalW = modes.length * badgeW + (modes.length - 1) * gap;
    const startX = RING_CENTER_X - totalW / 2;
    const y = 1;

    modes.forEach((mode, i) => {
      const x = startX + i * (badgeW + gap);
      const isActive = mode === activeMode;

      const badge = group.append('g')
        .attr('class', `${this.ringType}-mode-badge${isActive ? ' active' : ''}`)
        .attr('cursor', isActive ? 'default' : 'pointer');

      // Outline style for inactive, solid purple for active (like Chakra UI Badge)
      badge.append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', badgeW)
        .attr('height', badgeH)
        .attr('rx', 3)
        .attr('fill', isActive ? '#805AD5' : 'transparent')
        .attr('stroke', isActive ? '#805AD5' : '#A0AEC0')
        .attr('stroke-width', isActive ? 0 : 0.8);

      badge.append('text')
        .attr('x', x + badgeW / 2)
        .attr('y', y + badgeH / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-family', FONTS.chart.primary)
        .attr('font-size', '9px')
        .attr('font-weight', isActive ? '600' : '400')
        .attr('fill', isActive ? '#fff' : '#718096')
        .text(String(mode));

      // Click handler — emit event to switch mode
      if (!isActive) {
        badge.on('click', () => {
          globalEventStream.emit({
            id: `hora-mode-${chartId}-${Date.now()}`,
            type: 'hora.clock.mode.changed',
            source: 'CircularRingRenderer',
            timestamp: Date.now(),
            data: { chartId, hourMode: mode },
          });
        });
      }
    });
  }

  show(): void {
    this.layer.style('display', null);
    this.isVisible = true;
  }

  hide(): void {
    this.layer.style('display', 'none');
    this.isVisible = false;
  }

  clear(): void {
    this.layer.selectAll('*').remove();
  }

  destroy(): void {
    this.clear();
    this.isVisible = false;
  }

  getIsVisible(): boolean {
    return this.isVisible;
  }
}
